import { useCreateForeignContractorMutation } from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { BaseModalRef } from '@/app/helpers/types';
import { useOpen } from '@/app/hooks';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { FormTextInput } from '@/shared/inputs/form-text-input';
import ModalTemplate from '@/shared/modal/modal-template';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { Dialog, FormControl, Grid, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const DEFAULT_FORM_VALUES: FormValues = {
  name: '',
  tin: '',
  legalAddress: '',
  phone: '',
  email: '',
  bankName: '',
  bankCode: '',
  accountNumber: '',
  swift: '',
  iban: '',
};

interface FormValues {
  name: string;
  tin: string;
  legalAddress: string;
  phone: string;
  email: string;
  bankName: string;
  bankCode: string;
  accountNumber: string;
  swift: string;
  iban: string;
}

const CreateContractorModal = forwardRef<BaseModalRef>((_props, ref) => {
  const { t } = useTranslation();
  const { open, setOpen } = useOpen();
  const { showNotification } = useNotification();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useImperativeHandle(ref, () => ({
    open: () => onOpen(),
    close: () => onClose(),
  }));

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };
  const [createForeignContractor] = useCreateForeignContractorMutation();

  const onSubmit = async (data: FormValues) => {
    try {
      await createForeignContractor(data).unwrap();
      onClose();
      showNotification(t('foreign_contractor.success_added'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: 650, width: '100%' } }}
    >
      <ModalTemplate
        title={t('foreign_contractor.general_contact_data')}
        submitBtnText={t('foreign_contractor.save_and_add')}
        cancelBtnText={t('foreign_contractor.cancel')}
        onClose={onClose}
        onSubmit={handleSubmit(onSubmit)}
        loading={false}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.name')}:</UIFormLabel>
              <FormTextInput control={control} name='name' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.tin')}:</UIFormLabel>
              <FormTextInput control={control} name='tin' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>
                {t('foreign_contractor.legal_address')}:
              </UIFormLabel>
              <FormTextInput control={control} name='legalAddress' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.phone')}:</UIFormLabel>
              <FormTextInput control={control} name='phone' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.email')}:</UIFormLabel>
              <FormTextInput control={control} name='email' required />
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
          {t('foreign_contractor.bank_details')}:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.bank_name')}:</UIFormLabel>
              <FormTextInput control={control} name='bankName' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.bank_code')}:</UIFormLabel>
              <FormTextInput control={control} name='bankCode' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.account')}:</UIFormLabel>
              <FormTextInput control={control} name='accountNumber' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.swift')}:</UIFormLabel>
              <FormTextInput control={control} name='swift' required />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl required>
              <UIFormLabel>{t('foreign_contractor.iban')}:</UIFormLabel>
              <FormTextInput control={control} name='iban' required />
            </FormControl>
          </Grid>
        </Grid>
      </ModalTemplate>
    </Dialog>
  );
});

export default CreateContractorModal;
